import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { browserLocaleToPersist, isLocale } from '@arthelper/shared';

const STORAGE_KEY = 'preferred-locale';
const PENDING_KEY = `${STORAGE_KEY}-pending`;

export type ProfileLocaleSnapshot = {
  locale?: string | null;
  localeSource?: string | null;
};

/**
 * Syncs the user's profile locale to i18next and localStorage.
 * `profile` stays null until that profile has loaded. A saved locale is applied
 * locally. The browser locale is written back only when the loaded profile has
 * none. See shared/locale-sync.ts.
 */
export function useProfileLocaleSync(profile: ProfileLocaleSnapshot | null, onSaveLocale?: (locale: string) => void) {
  const { i18n } = useTranslation();
  const onSaveLocaleRef = useRef(onSaveLocale);
  onSaveLocaleRef.current = onSaveLocale;

  const loaded = profile != null;
  const locale = profile?.locale ?? null;
  const localeSource = profile?.localeSource ?? null;

  useEffect(() => {
    if (!loaded) return;
    const persist = browserLocaleToPersist({ locale, localeSource }, localStorage.getItem(STORAGE_KEY));
    if (!persist || !onSaveLocaleRef.current) return;
    onSaveLocaleRef.current(persist);
  }, [loaded, locale, localeSource]);

  useEffect(() => {
    if (!loaded || !locale || !isLocale(locale)) return;

    const stored = localStorage.getItem(STORAGE_KEY);
    const pending = localStorage.getItem(PENDING_KEY);

    if (stored === locale && i18n.language === locale) return;

    // A user-explicit locale change is in-flight (the profile mutation hasn't
    // returned yet). Don't revert; wait for the profile to catch up.
    if (pending && isLocale(pending) && pending !== locale) return;

    if (pending && pending === locale) {
      localStorage.removeItem(PENDING_KEY);
    }

    localStorage.setItem(STORAGE_KEY, locale);
    i18n.changeLanguage(locale);
  }, [loaded, locale, i18n]);
}
